import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Copy, KeyRound, Trash2 } from 'lucide-react';
import { api, ApiError, type Board } from '../../lib/api';
import { relativeTime } from '../../lib/time';
import { Modal, Spinner, toast } from '@vatman/ui';
import { errorText } from '../../i18n';
import { AgentLog } from '../../ui/AgentActivity';
import { ConfirmDialog } from './parts';
import { AgentAccounts } from './AgentAccounts';

export interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  /** Made by connecting an app (OAuth). */
  connection?: boolean;
  createdAt: number;
  lastUsedAt: number | null;
  expiresAt: number | null;
  /** Boards a limited token reaches; null = all the user's boards. */
  boards: { id: string; title: string }[] | null;
}

const tokensKey = ['api-tokens'];

/** The MCP endpoint people paste into Claude Code, Claude Desktop or another agent. */
export function mcpUrl(): string {
  return `${location.origin}/mcp`;
}

async function copy(text: string, done: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast(done);
  } catch {
    toast(text, 6000);
  }
}

type Level = 'none' | 'read' | 'write';
const LEVELS: Level[] = ['write', 'read', 'none'];

/** A token's access to a module from its scopes (`read`/`write` of old tokens cover every module). */
function levelOf(scopes: string[], module: 'boards' | 'tracker'): Level {
  if (scopes.includes('write') || scopes.includes(`${module}:write`)) return 'write';
  if (scopes.includes('read') || scopes.includes(`${module}:read`)) return 'read';
  return 'none';
}

/** A token just made: shown once, with the command that connects Claude Code. */
export type NewApiToken = ApiToken & { token: string };

/** Profile → AI agents: personal API tokens for the MCP endpoint, agent accounts, and what agents did. */
export function ApiTokensDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'tokens' | 'agents' | 'log'>('tokens');
  const tokens = useQuery({ queryKey: tokensKey, queryFn: () => api.get<ApiToken[]>('/tokens'), enabled: open });
  const [created, setCreated] = useState<NewApiToken | null>(null);
  const [revoking, setRevoking] = useState<ApiToken | null>(null);

  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/tokens/${id}`),
    onSuccess: (_, id) => {
      setCreated((c) => (c?.id === id ? null : c));
      void qc.invalidateQueries({ queryKey: tokensKey });
    },
    onError: (e) => toast(errorText(e instanceof ApiError ? e.code : 'internal')),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setCreated(null);
      }}
      title={t('tokens.title')}
      description={t('tokens.description')}
      width={640}
    >
      <div className="segmented" style={{ marginBottom: 16 }}>
        <button data-active={tab === 'tokens'} onClick={() => setTab('tokens')}>
          {t('tokens.tabTokens')}
        </button>
        <button data-active={tab === 'agents'} onClick={() => setTab('agents')}>
          {t('tokens.tabAgents')}
        </button>
        <button data-active={tab === 'log'} onClick={() => setTab('log')}>
          {t('tokens.tabLog')}
        </button>
      </div>

      {tab === 'log' ? (
        <AgentLog path="/tokens/activity" enabled={open} />
      ) : tab === 'agents' ? (
        <AgentAccounts enabled={open} />
      ) : created ? (
        <CreatedToken token={created} onDone={() => setCreated(null)} />
      ) : (
        <>
          <div className="tokens-oauth">
            <span>{t('tokens.connector')}</span>
            <div className="tokens-copy">
              <code className="tokens-code">{mcpUrl()}</code>
              <button type="button" className="btn sm" onClick={() => copy(mcpUrl(), t('common.copied'))}>
                <Copy size={15} /> {t('common.copy')}
              </button>
            </div>
          </div>
          <TokenForm endpoint="/tokens" limitBoards invalidate={tokensKey} onCreated={setCreated} />
        </>
      )}

      {tab === 'tokens' && (
        <div className="tokens-list">
          {tokens.isLoading && <Spinner />}
          {tokens.data?.length === 0 && <div className="muted">{t('tokens.empty')}</div>}
          {tokens.data?.map((tok) => <TokenRow key={tok.id} tok={tok} onRevoke={() => setRevoking(tok)} />)}
        </div>
      )}
      <ConfirmDialog
        open={!!revoking}
        onOpenChange={(v) => !v && setRevoking(null)}
        title={t('tokens.revokeTitle')}
        text={t('tokens.revokeText', { name: revoking?.name ?? '' })}
        confirmLabel={t('tokens.revoke')}
        onConfirm={() => revoking && revoke.mutate(revoking.id)}
      />
    </Modal>
  );
}

/**
 * A new token: name, access per module, expiry and, for a person's own token, which boards it
 * reaches. Posts to `endpoint` (`/tokens`, or an agent account's `/agents/{id}/tokens`).
 */
export function TokenForm({
  endpoint,
  limitBoards,
  invalidate,
  onCreated,
}: {
  endpoint: string;
  /** Offer "only these boards" (the board list is the signed-in person's). */
  limitBoards?: boolean;
  invalidate: readonly unknown[];
  onCreated: (tok: NewApiToken) => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('Claude');
  const [boardsAccess, setBoardsAccess] = useState<Level>('write');
  const [trackerAccess, setTrackerAccess] = useState<Level>('write');
  const [expires, setExpires] = useState<string>('90');
  const [scope, setScope] = useState<'all' | 'some'>('all');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const some = !!limitBoards && boardsAccess !== 'none' && scope === 'some';

  const create = useMutation({
    mutationFn: () =>
      api.post<NewApiToken>(endpoint, {
        name: name.trim(),
        modules: { boards: boardsAccess, tracker: trackerAccess },
        expiresInDays: expires === 'never' ? null : Number(expires),
        boards: some ? [...picked] : null,
      }),
    onSuccess: (tok) => {
      setPicked(new Set());
      void qc.invalidateQueries({ queryKey: invalidate });
      onCreated(tok);
    },
    onError: (e) => toast(errorText(e instanceof ApiError ? e.code : 'internal')),
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (name.trim() && (!some || picked.size > 0)) create.mutate();
  };

  return (
    <form className="tokens-form" onSubmit={submit}>
      <label className="field" style={{ flex: 2 }}>
        <span>{t('tokens.name')}</span>
        <input className="input" value={name} maxLength={100} onChange={(e) => setName(e.target.value)} placeholder="Claude Code" />
      </label>
      <label className="field" style={{ flex: 1.6 }}>
        <span>{t('tokens.moduleBoards')}</span>
        <select className="input" value={boardsAccess} onChange={(e) => setBoardsAccess(e.target.value as Level)}>
          {LEVELS.map((l) => (
            <option key={l} value={l}>
              {t(`tokens.level.${l}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="field" style={{ flex: 1.6 }}>
        <span>{t('tokens.moduleTracker')}</span>
        <select className="input" value={trackerAccess} onChange={(e) => setTrackerAccess(e.target.value as Level)}>
          {LEVELS.map((l) => (
            <option key={l} value={l}>
              {t(`tokens.level.${l}`)}
            </option>
          ))}
        </select>
      </label>
      <label className="field" style={{ flex: 1.4 }}>
        <span>{t('tokens.expires')}</span>
        <select className="input" value={expires} onChange={(e) => setExpires(e.target.value)}>
          <option value="30">{t('tokens.days', { count: 30 })}</option>
          <option value="90">{t('tokens.days', { count: 90 })}</option>
          <option value="365">{t('tokens.days', { count: 365 })}</option>
          <option value="never">{t('tokens.never')}</option>
        </select>
      </label>
      {limitBoards && boardsAccess !== 'none' && (
        <label className="field" style={{ flex: 2 }}>
          <span>{t('tokens.boards')}</span>
          <select className="input" value={scope} onChange={(e) => setScope(e.target.value as 'all' | 'some')}>
            <option value="all">{t('tokens.allBoards')}</option>
            <option value="some">{t('tokens.someBoards')}</option>
          </select>
        </label>
      )}
      {some && <BoardPicker picked={picked} onChange={setPicked} />}
      <button className="btn primary" disabled={create.isPending || !name.trim() || (some && picked.size === 0) || (boardsAccess === 'none' && trackerAccess === 'none')}>
        <KeyRound size={16} /> {t('tokens.create')}
      </button>
    </form>
  );
}

/** The token itself, once, and how to connect Claude Code with it. */
export function CreatedToken({ token, onDone }: { token: NewApiToken; onDone: () => void }) {
  const { t } = useTranslation();
  const command = `claude mcp add --transport http vatman ${mcpUrl()} --header "Authorization: Bearer ${token.token}"`;
  return (
    <div className="tokens-created">
      <div className="tokens-warning">{t('tokens.copyOnce')}</div>
      <label className="field">
        <span>{t('tokens.token')}</span>
        <div className="tokens-copy">
          <code className="tokens-code" data-testid="new-token">
            {token.token}
          </code>
          <button className="btn sm" onClick={() => copy(token.token, t('common.copied'))}>
            <Copy size={15} /> {t('common.copy')}
          </button>
        </div>
      </label>
      <label className="field">
        <span>{t('tokens.claudeCode')}</span>
        <div className="tokens-copy">
          <code className="tokens-code">{command}</code>
          <button className="btn sm" onClick={() => copy(command, t('common.copied'))}>
            <Copy size={15} /> {t('common.copy')}
          </button>
        </div>
      </label>
      <div className="muted" style={{ fontSize: 'var(--fs-3)' }}>
        {t('tokens.otherClients', { url: mcpUrl() })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button className="btn primary" onClick={onDone}>
          {t('common.done')}
        </button>
      </div>
    </div>
  );
}

/** One token in a list: what it reaches, when it was made and last used. */
export function TokenRow({ tok, onRevoke }: { tok: ApiToken; onRevoke: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="tokens-row">
      <KeyRound size={16} color="var(--text-3)" />
      <div style={{ minWidth: 0, flex: 1 }}>
        <div className="tokens-name">
          {tok.name}{' '}
          {(['boards', 'tracker'] as const).map((m) => {
            const l = levelOf(tok.scopes, m);
            return l === 'none' ? null : (
              <span key={m} className="badge">
                {t(m === 'boards' ? 'tokens.moduleBoards' : 'tokens.moduleTracker')}: {t(`tokens.level.${l}`).toLowerCase()}
              </span>
            );
          })}
          {tok.connection && <span className="badge">{t('tokens.connection')}</span>}
          {tok.boards && (
            <span className="badge" title={tok.boards.map((b) => b.title).join(', ')}>
              {t('tokens.boardsCount', { count: tok.boards.length })}
            </span>
          )}
        </div>
        <div className="muted tokens-meta">
          <code>{tok.prefix}…</code> · {t('tokens.created', { when: relativeTime(tok.createdAt) })} ·{' '}
          {tok.lastUsedAt ? t('tokens.used', { when: relativeTime(tok.lastUsedAt) }) : t('tokens.unused')}
          {tok.expiresAt ? ` · ${tok.expiresAt < Date.now() ? t('tokens.expired') : t('tokens.expiresAt', { when: relativeTime(tok.expiresAt) })}` : ''}
        </div>
        {tok.boards && <div className="muted tokens-meta tokens-boards">{tok.boards.map((b) => b.title).join(', ')}</div>}
      </div>
      <button className="btn sm ghost danger" onClick={onRevoke} title={t('tokens.revoke')}>
        <Trash2 size={15} /> {t('tokens.revoke')}
      </button>
    </div>
  );
}

/** Checklist of the user's boards for a board-limited token. */
function BoardPicker({ picked, onChange }: { picked: Set<string>; onChange: (s: Set<string>) => void }) {
  const { t } = useTranslation();
  const boards = useQuery({ queryKey: ['boards-for-token'], queryFn: () => api.get<Board[]>('/boards') });
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (boards.data ?? []).filter((b) => !needle || b.title.toLowerCase().includes(needle));
  }, [boards.data, q]);
  const flip = (id: string) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };
  return (
    <div className="tokens-picker">
      <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('tokens.findBoard')} />
      <div className="tokens-picker-list">
        {boards.isLoading && <Spinner />}
        {shown.map((b) => (
          <label key={b.id} className="tokens-picker-row">
            <input type="checkbox" checked={picked.has(b.id)} onChange={() => flip(b.id)} />
            <span>{b.title}</span>
            {b.teamName && <span className="muted">{b.teamName}</span>}
          </label>
        ))}
      </div>
      <div className="muted" style={{ fontSize: 'var(--fs-2)' }}>
        {t('tokens.picked', { count: picked.size })}
      </div>
    </div>
  );
}
